/*
 * Minicart redesign — the three behaviours that cannot be expressed in Liquid.
 *
 * Everything else the R - Minicart - Redesign variation did in JavaScript is markup here: the
 * banner, the order summary, the reassurance band, the checkout label, the stock countdown and the
 * disabled plus are all rendered by snippets/cart-drawer.liquid, in the order the design asks for.
 * What is left is three responses to events, which a template cannot have.
 *
 * Both are delegated from the document and read the DOM only — nothing here owns state, and neither
 * one runs unless the thing it responds to is actually on the page.
 */

(function () {
  'use strict';

  var DRAWER = '#CartDrawer';

  /* ── 1. The remove button's spinner ─────────────────────────────────────────────────────── */

  /*
   * Removing a line is a round trip, and the theme gives no feedback while it is in flight — on a
   * slow connection the button simply sits there and shoppers press it again. The class is what the
   * stylesheet's spinner keys off; the row is replaced wholesale when the cart re-renders, so
   * nothing has to take the class back off.
   */
  document.addEventListener('click', function (event) {
    var button = event.target.closest(DRAWER + ' cart-remove-button .cart-remove-button');
    if (!button) return;

    button.classList.add('vlb-mc-removing');
    var host = button.closest('cart-remove-button');
    if (host) host.classList.add('vlb-mc-removing');
  });

  /* ── 2. Keeping the shopper's place across a cart re-render ─────────────────────────────── */

  /*
   * Every cart change re-renders the drawer from the server, and the theme does it with
   * `targetElement.replaceWith(sourceElement)` on <cart-drawer-items> — the element that scrolls
   * (`overflow: auto` on it is the theme's own rule). Replacing it throws its scroll position away,
   * so a shopper who nudges the quantity on the fourth item, or adds from the upsell, is thrown
   * back to the top of their own cart.
   *
   * This predates the redesign and the experiment left it alone, but the redesign is what makes it
   * bite: the rows are 157px tall now, so the list starts scrolling at three or four items instead
   * of seven or eight.
   *
   * The position is remembered on scroll and put back on the element that replaces it. Nothing is
   * reset when the drawer closes: reopening it does not re-render, so the element keeps its own
   * position anyway, and after a change made elsewhere the last place the shopper was looking is
   * still the right answer. A position past the end of a now-shorter list is clamped by the browser.
   */
  var lastScrollTop = 0;

  // Capture phase, because scroll does not bubble.
  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (target && target.tagName === 'CART-DRAWER-ITEMS') lastScrollTop = target.scrollTop;
    },
    true
  );

  var drawer = document.querySelector(DRAWER);
  if (drawer) {
    new MutationObserver(function (records) {
      if (!lastScrollTop) return;

      for (var i = 0; i < records.length; i += 1) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var node = added[j];
          if (node.nodeType !== 1) continue;

          var items = node.tagName === 'CART-DRAWER-ITEMS' ? node : node.querySelector('cart-drawer-items');
          if (!items) continue;

          // Restored on the next frame: the element is in the document but has not been laid out
          // yet, so it has no scroll height to move within until the browser has measured it.
          requestAnimationFrame(function () {
            items.scrollTop = lastScrollTop;
          });
          return;
        }
      }
    }).observe(drawer, { childList: true, subtree: true });
  }

  /* ── 3. The upsell app's add, which the theme never hears about ─────────────────────────── */

  /*
   * The "Complete the set" card is injected by Essential Free Shipping, and its add button posts
   * /cart/add.js itself. It does not go through the theme's product form, so the theme is never
   * told — the cart gains a line and the drawer keeps showing the old list until something else
   * forces a render.
   *
   * There is no Liquid answer to another script's fetch, which is why this is the one piece of
   * behaviour that has to live here.
   *
   * IT RE-RENDERS THE DRAWER ITSELF rather than publishing the theme's cart-update event.
   *
   * Publishing was the first attempt and it was reported as a bug on 2026-09-24: adding from the
   * upsell updated the item list but left the total behind until the page was reloaded. The event's
   * only subscriber is the theme's `CartItems`, whose drawer branch replaces exactly two things —
   * `cart-drawer-items` and `.cart-drawer__footer`. The redesign's order summary sits outside both
   * (the theme's own footer is hidden when the redesign is on), so the total was never in the set
   * being replaced, and neither was the header's count bubble. A quantity change looked fine only
   * because that path goes through `CartDrawerItems`, which re-renders `.drawer__inner` whole.
   *
   * So this does what a quantity change does — the sections the drawer itself asks for, rendered
   * into the same two places — which keeps the total, the item list and the bubble in step.
   */
  var POLL_INTERVAL = 400;
  var POLL_ATTEMPTS = 8;

  /** How many units the drawer is currently showing, summed across its quantity fields. */
  function renderedUnits() {
    var inputs = document.querySelectorAll(DRAWER + ' .quantity__input');
    return Array.prototype.reduce.call(
      inputs,
      function (sum, input) {
        var value = parseInt(input.value, 10);
        return sum + (isFinite(value) ? value : 0);
      },
      0
    );
  }

  /**
   * Re-renders the drawer and the header count from freshly rendered sections.
   *
   * Deliberately NOT the drawer's own `renderContents`, which would do the same two sections and
   * then call `open()` on a drawer that is already open — adding another one-shot transitionend
   * focus trap each time the shopper adds from the upsell.
   */
  function redrawDrawer() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + '?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (sections) {
        if (!sections) return;

        var innerFrom = function (html, selector) {
          if (!html) return null;
          var found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
          return found ? found.innerHTML : null;
        };

        // The whole drawer body, which is what carries the order summary and its total.
        var inner = document.querySelector('.drawer__inner');
        var freshInner = innerFrom(sections['cart-drawer'], '.drawer__inner');
        if (inner && freshInner !== null) inner.innerHTML = freshInner;

        // The header's count, which the old path was not updating either.
        var bubble = document.getElementById('cart-icon-bubble');
        var freshBubble = innerFrom(sections['cart-icon-bubble'], '.shopify-section');
        if (bubble && freshBubble !== null) bubble.innerHTML = freshBubble;
      })
      .catch(function () {
        /* the section render is unreachable; the next real cart action will re-render anyway */
      });
  }

  /**
   * Waits for the app's add to land, then asks the theme to re-render.
   *
   * Polled rather than delayed by a fixed amount, because the app's request takes as long as it
   * takes and a single guessed timeout is either too early or needlessly slow. It gives up after a
   * few seconds: by then the add has either failed or the shopper has moved on, and a re-render
   * that arrives late is worse than none — it would throw away whatever they did in between.
   */
  function refreshWhenCartGrows() {
    var before = renderedUnits();
    var attempts = 0;

    var tick = function () {
      attempts += 1;

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          if (!cart) return;

          // The drawer is behind the cart: the app's line has landed and is not being shown.
          if (cart.item_count > before) {
            redrawDrawer();
            return;
          }

          if (attempts < POLL_ATTEMPTS) setTimeout(tick, POLL_INTERVAL);
        })
        .catch(function () {
          /* the cart is unreachable; the next real cart action will re-render anyway */
        });
    };

    setTimeout(tick, POLL_INTERVAL);
  }

  /*
   * The app's own add button. Matched on the stable part of its hashed class, exactly as the
   * stylesheet does — see the note over the upsell block in minicart-redesign.css for why that
   * coupling is accepted and where else it lives.
   */
  document.addEventListener('click', function (event) {
    var cta = event.target.closest(DRAWER + ' a[class*="free_shipping_card_product_cta_"]');
    if (!cta) return;

    refreshWhenCartGrows();
  });
})();
